import React, { ReactElement, useEffect, useRef, useState } from 'react';
import {
  Heading,
  Button,
  Form,
  DropdownOptionProps,
} from '@appsumo/dorado-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';
import {
  STEP,
  COUNTRIES_LIST,
  COUNTRIES_REQUIRE_POSTAL_CODE,
} from '~/constants/checkout';
import { useEvent } from '~/lib/events';

import { CheckoutCard, Loading } from '../Global';
import { TaxIdCollection } from './subcomponents';
import { completeBillingSchema } from '~/schemas/billing';
import { setFormErrors } from './util';
import { Loader } from '~/components/ui';
import { BillingDetailsFormProps, FormRefProps } from '~/types/checkout';

function BillingDetails({
  isCreditsCouponsEnabled = false,
}: Readonly<{
  isCreditsCouponsEnabled: boolean;
}>): ReactElement {
  const ref = useRef<FormRefProps>();
  const {
    setCurrentBillingCountry,
    setStep,
    setStepErrorMessage,
    setStepValidation,
    submitBillingDetails,
  } = useCheckoutAPI();
  const { billingDetails, isBillingLoading, step, stepErrorMessageObject } =
    useCheckoutState();
  const [showTaxCollection, setShowTaxCollection] = useState<boolean>(
    !!billingDetails.taxId,
  );
  const [currentCountry, setCurrentCountry] = useState<DropdownOptionProps>(
    billingDetails.country || {
      value: '',
      label: '',
    },
  );
  // Added isBillingLoaded so that the form validation works properly upon page load
  const errorMessage = stepErrorMessageObject[STEP.BILLING];

  // Update billing country if it's not set
  // This is needed when logging in on checkout page
  useEffect(() => {
    if (!currentCountry.value && billingDetails.country) {
      setCurrentCountry(billingDetails.country);
      setCurrentBillingCountry(`${billingDetails.country.value}`);
      setShowTaxCollection(!!billingDetails.taxId);
    }
  }, [
    billingDetails.country,
    billingDetails.taxId,
    currentCountry.value,
    setCurrentBillingCountry,
  ]);

  // This is used to get the billing country to the klarna component
  useEffect(() => {
    setCurrentBillingCountry(`${currentCountry.value}`);
  }, [currentCountry.value, setCurrentBillingCountry]);

  if (step !== STEP.BILLING) return <></>;

  const onClickSubmitBillingDetails = async (
    data: BillingDetailsFormProps,
  ): Promise<void> => {
    setStepErrorMessage(STEP.BILLING, '');

    const [error] = await submitBillingDetails(data, showTaxCollection);

    if (!error) {
      setStep(isCreditsCouponsEnabled ? STEP.CREDITS_COUPONS : STEP.PAYMENT);
      setStepValidation(STEP.BILLING, true);

      return;
    }

    setStepValidation(STEP.BILLING, false);

    let unknownError =
      'Cannot update billing details at this time. Please try again later.';

    if (error?.response?.status === 400) {
      const errors = await error.response.json();

      if (setFormErrors(errors, ref)) return;

      if (errors) {
        if (typeof errors === 'string' || errors instanceof String) {
          unknownError = `${errors}`;
        } else if (Object.keys(errors).length) {
          unknownError = `${Object.keys(errors)[0]} - ${
            Object.values(errors)[0]
          }`;
        }
      }
    }

    setStepErrorMessage(STEP.BILLING, unknownError);
  };

  function onChangeBilling(data: FieldValues, name: string, type: string) {
    if (type === 'change') {
      setStepErrorMessage(STEP.BILLING, '');
      setStepValidation(STEP.BILLING, false);

      if (name === 'country') {
        setCurrentCountry(data.country);

        if (data.country.value !== 'AU' && ref?.current?.setValue) {
          ref.current.setValue('save_tax_info', false);
        }
      }
    }
  }

  function toggleTaxCollection() {
    const newValue = !showTaxCollection;

    setShowTaxCollection(newValue);

    // Needed because of typing
    if (ref?.current?.setValue) {
      ref.current.setValue('save_tax_info', newValue);
    }

    if (!newValue && ref?.current?.clearErrors) {
      ref.current.clearErrors('tax_id');
      ref.current.clearErrors('tax_business_name');
    }
  }

  const stepButtonText = isCreditsCouponsEnabled
    ? 'Continue to credits and coupons'
    : 'Continue to payment method';

  return (
    <>
      <Heading.H3 className="mb-6">
        {errorMessage && (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            height="16"
            className="mr-2 text-brick"
          />
        )}
        <span>Billing information</span>
      </Heading.H3>
      <div className="w-full">
        {/* The defaultValue(s) can only be set ONCE, so we need to set it after the initial billingDetails are loaded */}
        {/* Show a loading icon while loading the initial details */}
        {currentCountry.value && (
          <Form
            onSubmit={onClickSubmitBillingDetails}
            resolver={yupResolver(completeBillingSchema)}
            ref={ref}
            autoClearErrors
            watch={onChangeBilling}
          >
            <div className="flex flex-col gap-2">
              <div data-testid="billing-checkout-form-container">
                <Form.Dropdown
                  name="country"
                  label="Country"
                  options={COUNTRIES_LIST}
                  defaultValue={billingDetails.country}
                  heightLimit={200}
                />
                {COUNTRIES_REQUIRE_POSTAL_CODE.includes(
                  `${currentCountry.value}`,
                ) && (
                  <Form.Input
                    name="postal_code"
                    label="Postal code"
                    type="text"
                    defaultValue={billingDetails.postalCode}
                  />
                )}
              </div>
              <TaxIdCollection
                isCountryAus={currentCountry.value === 'AU'}
                show={showTaxCollection}
                toggleTaxCollection={toggleTaxCollection}
                billingDetails={billingDetails}
              />
              <Form.Checkbox
                name="save_billing_info"
                label="Save for future purchases"
                // @ts-ignore -- TODO: Add this to the checkbox props
                defaultChecked={billingDetails.saveBillingInfo}
              />
            </div>
            <Button
              type="submit"
              className="mt-6 flex w-full justify-center md:w-fit"
              disabled={isBillingLoading}
              data-testid="billing-checkout-form-submit"
            >
              <span>{stepButtonText}</span>
              {isBillingLoading && (
                <span className="ml-2">
                  <Loader size="sm" />
                </span>
              )}
            </Button>
            <Form.Input
              type="hidden"
              name="save_tax_info"
              defaultValue={
                currentCountry?.value === 'AU' && billingDetails.taxId
                  ? 'true'
                  : 'false'
              }
            />
          </Form>
        )}
        <Loading isLoading={!currentCountry.value} />
      </div>
    </>
  );
}

function BillingDetailsMinimized(): ReactElement {
  const { step } = useCheckoutState();

  if (step !== 'account') return <></>;

  return <Heading.H3 className="text-slate">Billing information</Heading.H3>;
}

function BillingDetailsCompleted(): ReactElement {
  const { setStep, setStepErrorMessage } = useCheckoutAPI();
  const { billingDetails, step } = useCheckoutState();
  const [isPaymentPending, setIsPaymentPending] = useState(false);

  useEvent('payment:pending', () => setIsPaymentPending(true));
  useEvent('payment:reset', () => setIsPaymentPending(false));

  if (step !== STEP.PAYMENT && step !== STEP.CREDITS_COUPONS) return <></>;

  const onClickEdit = (e: React.MouseEvent<EventTarget>): void => {
    e.preventDefault();

    if (isPaymentPending) return;

    setStepErrorMessage(STEP.PAYMENT, ''); // Reset the payment error since we are going back to the billing step
    setStep(STEP.BILLING);
  };

  const postalCodeText = COUNTRIES_REQUIRE_POSTAL_CODE.includes(
    `${billingDetails?.country?.value}`,
  )
    ? `, ${billingDetails?.postalCode}`
    : '';
  const hasTaxDetails =
    billingDetails?.country?.value === 'AU' &&
    billingDetails?.taxId &&
    billingDetails?.showTaxCollection;

  return (
    <>
      <div className="flex w-full">
        <div className="grow">
          <Heading.H3 data-testid="billing-details-title">
            Billing information
          </Heading.H3>
        </div>
        <button onClick={onClickEdit} className="text-bolt hover:underline">
          Edit
        </button>
      </div>
      <div className="mt-4">
        <div>
          {billingDetails?.country?.label}
          {postalCodeText}
        </div>
      </div>

      {hasTaxDetails && (
        <div className="mt-4">
          <div>
            {billingDetails?.taxBusinessName}, {billingDetails.taxId}
          </div>
        </div>
      )}
    </>
  );
}

export default function Billing({
  isCreditsCouponsEnabled = false,
}: Readonly<{
  isCreditsCouponsEnabled: boolean;
}>): ReactElement {
  return (
    <CheckoutCard step={STEP.BILLING}>
      <BillingDetails isCreditsCouponsEnabled={isCreditsCouponsEnabled} />
      <BillingDetailsCompleted />
      <BillingDetailsMinimized />
    </CheckoutCard>
  );
}
